import React, { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { TabsContainer } from '../shared/TabsContainer';
import { isTouchDevice } from '../../theme';

export type TabType =
    | 'all'
    | 'general'
    | 'physical-properties'
    | 'rules-regulations'
    | 'operational'
    | 'regulations'
    | 'uscg'
    | 'coating'
    | 'safety'
    | 'cleaning'
    | 'other-procedures'
    | 'external'
    | 'orca'
    | 'changelog';

interface TabMenuProps {
    activeTab: TabType;
    onTabChange: (tab: TabType) => void;
    hasWriterRole?: boolean;
}

const MOBILE_BREAKPOINT = 768;

/** Mobile tabs (Physical Properties, Rules & Regulations) are only shown
 *  when the sidebar is hidden — which requires BOTH a narrow viewport AND
 *  a touch device. This prevents laptops with large font sizes from
 *  incorrectly switching to the mobile tab set. */
const isMobileView = () =>
    window.innerWidth <= MOBILE_BREAKPOINT && isTouchDevice();

const TabMenu: React.FC<TabMenuProps> = ({ activeTab, onTabChange, hasWriterRole = false }) => {
    const [isMobile, setIsMobile] = useState(isMobileView);

    useEffect(() => {
        const handleResize = () => setIsMobile(isMobileView());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const tabs = useMemo(
        () => {
            const allTabs = [
                { id: 'all', label: 'All' },
                { id: 'general', label: 'General' },
                { id: 'physical-properties', label: 'Physical Properties', mobileOnly: true },
                { id: 'rules-regulations', label: 'Rules & Regulations', mobileOnly: true },
                { id: 'operational', label: 'Operational' },
                { id: 'regulations', label: 'Regulations & Requirements' },
                { id: 'uscg', label: 'USCG' },
                { id: 'coating', label: 'Coating' },
                { id: 'safety', label: 'Safety/Emergency' },
                { id: 'cleaning', label: 'Cleaning' },
                { id: 'other-procedures', label: 'Other Procedures' },
                { id: 'external', label: 'External IDs' },
                // { id: 'orca', label: 'ORCA' }, // Hidden - not in use
                ...(hasWriterRole ? [{ id: 'changelog', label: 'Change Log', writerOnly: true }] : []),
            ];
            return isMobile ? allTabs : allTabs.filter(t => !t.mobileOnly);
        },
        [isMobile, hasWriterRole]
    );
    const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

    const focusTab = (tabId: TabType) => {
        const target = tabRefs.current[tabId];
        if (target) {
            target.focus();
        }
    };

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key !== 'Tab') return;

            event.preventDefault();
            const currentIndex = tabs.findIndex((tab) => tab.id === activeTab);
            const nextIndex = event.shiftKey
                ? (currentIndex - 1 + tabs.length) % tabs.length
                : (currentIndex + 1) % tabs.length;
            const nextTab = tabs[nextIndex].id as TabType;
            onTabChange(nextTab);
            focusTab(nextTab);
        },
        [activeTab, onTabChange, tabs]
    );

    return (
        <TabsContainer role="tablist" onKeyDown={handleKeyDown}>
            {tabs.map((tab) => (
                <li key={tab.id} className={tab.id === 'changelog' ? 'tab-right' : undefined}>
                    <button
                        type="button"
                        className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                        role="tab"
                        aria-selected={activeTab === tab.id}
                        ref={(node) => {
                            tabRefs.current[tab.id] = node;
                        }}
                        onClick={() => onTabChange(tab.id as TabType)}
                    >
                        {tab.label}
                    </button>
                </li>
            ))}
        </TabsContainer>
    );
};

export default TabMenu;
